import { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, Button, Spinner, Badge, InputGroup, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getServicesByBranch } from '../../../../../services/PublicBookingService';
import { formatNameList, formatNumberWithTwoDecimals } from '../../../../../utils/FormatUtils';

// A partir de quantos servicos a busca aparece. Com poucas opcoes o campo so
// atrapalha o cliente, que enxerga a lista inteira de uma vez.
const SEARCH_MIN_SERVICES = 6;

// Serviço sem categoria cadastrada vai para um grupo próprio, em vez de ficar de fora das abas.
const NO_CATEGORY = 'Outros';
const categoryOf = (service) => service.categories?.[0] || NO_CATEGORY;

/** 90 vira "1h30", 45 vira "45 min": o tempo total lido de relance no rodapé. */
const formatDuration = (minutes) => {
    const total = Math.max(0, Math.round(minutes));
    if (total < 60) return `${total} min`;
    const hours = Math.floor(total / 60);
    const rest = total % 60;
    return rest === 0 ? `${hours}h` : `${hours}h${String(rest).padStart(2, '0')}`;
};

// Ignora acentos e caixa: o cliente digita "maquina" e acha "Máquina".
const normalize = (value) => (value || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/**
 * Desconto de 100% é "incluso no plano"; abaixo disso o assinante ainda paga uma parte, e
 * mostrar "incluso" seria promessa que o preço final não cumpre.
 */
const isFullyCovered = (discountPercentage) => Number(discountPercentage) >= 100;

const StepService = ({ companyUuid, branchId, onSelect, onBack, selectedServices, customerData }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [services, setServices] = useState([]);
    const [selected, setSelected] = useState(selectedServices || []);
    const [search, setSearch] = useState('');
    // Categoria escolhida nas abas; vazio mostra todas, agrupadas.
    const [category, setCategory] = useState('');
    const [error, setError] = useState('');

    const showSearch = services.length >= SEARCH_MIN_SERVICES;

    // Cobertura do plano do cliente identificado no passo anterior, indexada por serviço.
    const planCoverage = useMemo(() => {
        const coverage = new Map();
        (customerData?.planProducts || []).forEach((planProduct) => {
            coverage.set(planProduct.productId, planProduct.discountPercentage);
        });
        return coverage;
    }, [customerData]);

    const isClubMember = Boolean(customerData?.subscriptionCustomer);
    // Plano que só dá benefício em certos dias: os valores abaixo valem para esses dias.
    const benefitDays = customerData?.planBenefitDays || [];
    const offDayDiscount = Number(customerData?.planOffDayDiscount || 0);
    const offDayNotice = benefitDays.length > 0
        ? (offDayDiscount > 0
            ? `Estes valores valem em ${benefitDays.join(', ')}. Nos demais dias o desconto é de ${formatNumberWithTwoDecimals(offDayDiscount)}%.`
            : `Estes valores valem em ${benefitDays.join(', ')}. Nos demais dias os serviços saem pelo valor cheio.`)
        : null;

    // Cota esgotada: o serviço continua no plano, mas neste ciclo sai pelo preço cheio.
    const benefitsExhausted = isClubMember
        && !customerData?.unlimitedBenefits
        && Number(customerData?.remainingBenefits ?? 0) <= 0;

    // Quantos serviços do plano ainda cabem na cota deste ciclo; plano ilimitado não tem teto.
    const remainingQuota = customerData?.unlimitedBenefits
        ? Infinity
        : Number(customerData?.remainingBenefits ?? 0);

    // Serviços escolhidos que vão receber o benefício. O backend aplica a cota aos primeiros
    // serviços do plano na ordem do pedido, que é a ordem em que o cliente foi marcando; a
    // prévia segue a mesma regra para não prometer desconto que a confirmação vai tirar.
    const benefitServiceIds = useMemo(() => {
        const ids = new Set();
        selected.forEach((service) => {
            if (planCoverage.has(service.id) && ids.size < remainingQuota) {
                ids.add(service.id);
            }
        });
        return ids;
    }, [selected, planCoverage, remainingQuota]);

    // Escolhido: só se ficou entre os que a cota cobre. Ainda não escolhido: só se sobra cota
    // para ele, que entraria no fim da fila ao ser marcado.
    const receivesBenefit = (service) => (isSelected(service.id)
        ? benefitServiceIds.has(service.id)
        : benefitServiceIds.size < remainingQuota);

    // Cota restante toda comprometida com a seleção: explica por que os demais serviços do
    // plano passaram a mostrar o valor normal.
    const quotaNotice = isClubMember && !benefitsExhausted && Number.isFinite(remainingQuota)
        && benefitServiceIds.size >= remainingQuota
        ? `${remainingQuota === 1 ? 'Seu benefício restante já vai para' : 'Seus benefícios restantes já vão para'} ${formatNameList(selected.filter(s => benefitServiceIds.has(s.id)).map(s => s.name))}. Os demais serviços do plano saem pelo valor normal neste agendamento.`
        : null;

    const getServicePrice = (service) => {
        const discount = planCoverage.get(service.id);
        if (!isClubMember || benefitsExhausted || !discount || !receivesBenefit(service)) {
            return { price: service.price || 0, discount: 0 };
        }
        const original = service.price || 0;
        return { price: original - (original * Number(discount)) / 100, discount: Number(discount) };
    };

    const filteredServices = useMemo(() => {
        const term = normalize(search);
        if (!term) return services;
        // A descrição do serviço vem em "detail"; procurar em "description" nunca achava nada.
        return services.filter(s => normalize(s.name).includes(term)
            || normalize(s.detail).includes(term));
    }, [services, search]);

    // Abas: uma por categoria, na ordem em que aparecem, com quantos serviços ela tem e quantos
    // já foram marcados (o cliente não perde a conta de uma escolha ao trocar de aba).
    const categoryOptions = useMemo(() => {
        const options = new Map();
        services.forEach((service) => {
            const name = categoryOf(service);
            const option = options.get(name) || { name, count: 0, selected: 0 };
            option.count += 1;
            if (selected.some((item) => item.id === service.id)) option.selected += 1;
            options.set(name, option);
        });
        return [...options.values()];
    }, [services, selected]);
    const showCategories = categoryOptions.length >= 2;

    // O que a lista mostra: a busca e a aba escolhida filtram; sem nenhuma das duas, os
    // serviços saem agrupados por categoria para o cliente percorrer por assunto.
    const groups = useMemo(() => {
        const visible = filteredServices.filter((service) => !category || categoryOf(service) === category);
        if (!showCategories || category || normalize(search)) {
            return [{ name: null, items: visible }];
        }
        const byCategory = new Map();
        visible.forEach((service) => {
            const name = categoryOf(service);
            if (!byCategory.has(name)) byCategory.set(name, []);
            byCategory.get(name).push(service);
        });
        return [...byCategory].map(([name, items]) => ({ name, items }));
    }, [filteredServices, category, showCategories, search]);
    const visibleCount = groups.reduce((total, group) => total + group.items.length, 0);

    useEffect(() => {
        loadServices();
    }, [branchId, companyUuid]);

    const loadServices = async () => {
        try {
            setLoading(true);
            const response = await getServicesByBranch(companyUuid, branchId);
            // A API já devolve somente registros classificados como SERVICE.
            setServices(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            setError(t('booking.load_services_error'));
        } finally {
            setLoading(false);
        }
    };

    const toggleService = (service) => {
        setSelected(prev => {
            const exists = prev.find(s => s.id === service.id);
            if (exists) {
                return prev.filter(s => s.id !== service.id);
            }
            return [...prev, service];
        });
    };

    const isSelected = (serviceId) => {
        return selected.some(s => s.id === serviceId);
    };

    const getTotalDuration = () => {
        return selected.reduce((total, s) => total + (s.duration || 30), 0);
    };

    const getTotalPrice = () => {
        return selected.reduce((total, s) => total + getServicePrice(s).price, 0);
    };

    // Basta um serviço de preço aberto para o total deixar de ser fechado. Sem o aviso, o
    // cliente sai do site com um número na cabeça e a conversa na cadeira começa errada.
    const hasPriceFrom = () => selected.some(service => service.priceFrom === true);

    const handleContinue = () => {
        if (selected.length > 0) {
            onSelect(selected);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">{t('booking.loading_services')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-5">
                <i className="fa fa-exclamation-circle fa-3x text-danger mb-3"></i>
                <p className="text-danger">{error}</p>
                <Button variant="outline-primary" onClick={onBack}>
                    <i className="fa fa-arrow-left me-2"></i>
                    {t('back')}
                </Button>
            </div>
        );
    }

    if (!services || services.length === 0) {
        return (
            <div className="text-center py-5">
                <i className="fa fa-list fa-3x text-muted mb-3"></i>
                <p className="text-muted">{t('booking.no_services')}</p>
                <Button variant="outline-primary" onClick={onBack}>
                    <i className="fa fa-arrow-left me-2"></i>
                    {t('back')}
                </Button>
            </div>
        );
    }

    return (
        <div className="step-service">
            <div className="step-header mb-4">
                <h4 className="step-title">
                    <i className="fa fa-list me-2 text-primary"></i>
                    {t('booking.select_services')}
                </h4>
                <p className="step-subtitle text-muted">
                    {t('booking.select_services_description')}
                </p>
            </div>

            {isClubMember && (
                <div className="booking-club-banner mb-3">
                    <i className="fa fa-crown"></i>
                    <div>
                        {benefitsExhausted ? (
                            <>
                                <strong>Benefícios do clube já usados neste ciclo.</strong>
                                <span>Os serviços do plano voltam a ficar inclusos no próximo ciclo.</span>
                            </>
                        ) : (
                            <>
                                <strong>
                                    Benefícios do clube ativos
                                    {customerData?.unlimitedBenefits
                                        ? ''
                                        : `, ${customerData.remainingBenefits} restante(s) neste ciclo`}.
                                </strong>
                                {/* A data só é escolhida dois passos adiante, então os preços
                                    aqui assumem um dia coberto pelo plano. Dizer o critério
                                    agora evita a surpresa de ver outro valor na confirmação. */}
                                <span>
                                    {[quotaNotice, offDayNotice].filter(Boolean).join(' ')
                                        || 'O preço final é confirmado no último passo.'}
                                </span>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Busca e abas ficam presas no topo enquanto a lista rola: com muitos serviços o
                cliente troca de assunto sem voltar ao começo da página. */}
            {(showSearch || showCategories) && (
            <div className="service-toolbar">
            {showSearch && (
                <InputGroup className="service-search mb-2">
                    <InputGroup.Text className="border-end-0">
                        <i className="fa fa-search text-muted"></i>
                    </InputGroup.Text>
                    <Form.Control
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('booking.search_service_placeholder')}
                        aria-label={t('booking.search_service_placeholder')}
                        className="border-start-0 ps-0"
                    />
                    {search && (
                        <Button
                            variant="light"
                            className="service-search-clear"
                            onClick={() => setSearch('')}
                            aria-label={t('booking.clear_search')}
                        >
                            <i className="fa fa-times"></i>
                        </Button>
                    )}
                </InputGroup>
            )}
            {showCategories && (
                <div className="service-categories" role="tablist" aria-label="Categorias de serviço">
                    <button type="button" role="tab" aria-selected={!category}
                        className={`service-category ${!category ? 'is-active' : ''}`} onClick={() => setCategory('')}>
                        Todos
                    </button>
                    {categoryOptions.map((option) => (
                        <button type="button" role="tab" key={option.name} aria-selected={category === option.name}
                            className={`service-category ${category === option.name ? 'is-active' : ''}`}
                            onClick={() => setCategory(option.name)}>
                            {option.name}
                            {option.selected > 0 && <span className="service-category__count">{option.selected}</span>}
                        </button>
                    ))}
                </div>
            )}
            </div>
            )}

            {/* Filtro so esconde os cards: os servicos ja marcados continuam em
                "selected" e seguem contando no resumo e no total. */}
            {visibleCount === 0 ? (
                <div className="text-center py-4 mb-4">
                    <i className="fa fa-search fa-2x text-muted mb-3 d-block"></i>
                    <p className="text-muted mb-3">
                        {t('booking.no_services_found', { term: search })}
                    </p>
                    <Button variant="link" size="sm" onClick={() => { setSearch(''); setCategory(''); }}>
                        {t('booking.clear_search')}
                    </Button>
                </div>
            ) : (
            <div className="mb-2">
            {groups.map((group) => (
            <section key={group.name || 'all'} className="service-group">
            {group.name && (
                <h5 className="service-group__title">
                    {group.name}
                    <small>{group.items.length}</small>
                </h5>
            )}
            <Row className="g-3 mb-4">
                {group.items.map((service) => (
                    <Col md={6} key={service.id}>
                        <Card
                            className={`service-card h-100 cursor-pointer ${isSelected(service.id) ? 'selected' : ''}`}
                            onClick={() => toggleService(service)}
                            /* O card inteiro é o alvo de toque, mas quem usa leitor de tela ou
                               teclado precisa saber que são várias escolhas, não uma só. */
                            role="checkbox"
                            aria-checked={isSelected(service.id)}
                            tabIndex={0}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    toggleService(service);
                                }
                            }}
                        >
                            <Card.Body className="d-flex align-items-center p-3">
                                {/* Quadrado, não círculo: forma de checkbox comunica "pode
                                    escolher vários" antes de qualquer texto. */}
                                <span className="service-check me-3" aria-hidden="true">
                                    <i className="fa fa-check"></i>
                                </span>
                                <div className="service-info flex-grow-1">
                                    <h6 className="service-name mb-1">
                                        {service.name}
                                        {planCoverage.has(service.id) && (
                                            <Badge bg="warning" text="dark" className="ms-2 service-club-badge">
                                                <i className="fa fa-crown me-1"></i>
                                                {isFullyCovered(planCoverage.get(service.id))
                                                    ? 'Incluso no clube'
                                                    : `-${formatNumberWithTwoDecimals(planCoverage.get(service.id))}%`}
                                            </Badge>
                                        )}
                                    </h6>
                                    {service.detail && <p className="service-description">{service.detail}</p>}
                                    <div className="service-details d-flex gap-3">
                                        <span className="text-muted small">
                                            <i className="fa fa-clock me-1"></i>
                                            {service.duration || 30} min
                                        </span>
                                        {(() => {
                                            const { price, discount } = getServicePrice(service);
                                            // "A partir de" acompanha o preço cheio: no
                                            // benefício do plano quem manda é o desconto, e
                                            // "incluso" não admite ponto de partida.
                                            const prefix = service.priceFrom ? 'A partir de ' : '';
                                            if (!discount) {
                                                return (
                                                    <span className="text-primary fw-semibold">
                                                        {prefix}R$ {formatNumberWithTwoDecimals(service.price || 0)}
                                                    </span>
                                                );
                                            }
                                            return (
                                                <span className="fw-semibold">
                                                    <s className="text-muted me-2">
                                                        R$ {formatNumberWithTwoDecimals(service.price || 0)}
                                                    </s>
                                                    <span className="text-success">
                                                        {isFullyCovered(discount)
                                                            ? 'Incluso'
                                                            : `R$ ${formatNumberWithTwoDecimals(price)}`}
                                                    </span>
                                                </span>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>
            </section>
            ))}
            </div>
            )}

            {/* Rodapé fixo: o resumo da escolha e os botões andam juntos. O total fica sempre à
                vista enquanto o cliente percorre a lista, em vez de ficar escondido no fim dela. */}
            <div className="step-footer">
                <div className={`step-footer__summary ${selected.length === 0 ? 'is-empty' : ''}`} aria-live="polite">
                    {selected.length === 0 ? (
                        <span>Selecione um ou mais serviços</span>
                    ) : (
                        <>
                            <span className="step-footer__count">
                                {selected.length} {selected.length === 1 ? t('booking.service') : t('booking.services')}
                            </span>
                            <span className="step-footer__time">
                                <i className="fa fa-clock"></i> {formatDuration(getTotalDuration())}
                            </span>
                            <span className="step-footer__total">
                                {t('total')}: {hasPriceFrom() ? 'a partir de ' : ''}R$ {formatNumberWithTwoDecimals(getTotalPrice())}
                            </span>
                        </>
                    )}
                </div>

                {/* Botões de navegação */}
                <div className="step-actions d-flex justify-content-between">
                    <Button variant="outline-secondary" onClick={onBack}>
                        <i className="fa fa-arrow-left me-2"></i>
                        {t('back')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleContinue}
                        disabled={selected.length === 0}
                    >
                        {t('continue')}
                        <i className="fa fa-arrow-right ms-2"></i>
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default StepService;
